import * as React from 'react';
import PageModel from '@app/Model/PageModel';
import { PageSection, Title, Text, TextVariants, Button, Flex, FlexItem } from '@patternfly/react-core';
import BlueDot from '@app/Components/Report/BlueDot';
import SmartTest from '@app/Components/Report/SmartTest';
import Degrades from '@app/Components/Report/Degrades';
import QualityPremiums from '@app/Components/Report/QualityPremiums';
import HistoricalQualityComponents from '@app/Components/Report/HistoricalQualityComponents';
import CurrentQuality from '@app/Components/Report/CurrentQuality';
import MemberEquity from '@app/Components/Report/MemberEquity';
import MontanaPricing from '@app/Components/Report/MontanaPricing';
import CoopPricing from '@app/Components/Report/CoopPricing';
import DailyComponents from '@app/Components/Report/DailyComponents';
import FCPSettlements from '@app/Components/Report/FCPSettlements';
import FCPOpenContracts from '@app/Components/Report/FCPOpenContracts';
import PayrollList from '@app/Components/Report/Payroll';
import DDCPayrollReport from '@app/Components/Report/DDCPayrollReport';
import EStatements from '@app/Components/Report/eStatements';
import DailyAverageProductionBase from '@app/Components/Report/DailyAverageProductionBase';
import ProjectedMilkValue from '@app/Components/Report/ProjectedMilkValue/Report';
import ReportDataHelper from '@app/Service/Report/ReportDataHelper';
import { UserContext } from '@app/Context/UserContext';
import { NotFound } from '@app/NotFound/NotFound';
import TabulatorReferenceInterface from '@app/Interface/TabulatorReferenceInterface';
import DownloadIcon from '@patternfly/react-icons/dist/js/icons/download-icon';
import htmlParser from '@app/utils/htmlParser';

interface ReportPageProps {
    pageData: PageModel,
}

const ReportPage: React.FC<ReportPageProps> = ({ pageData, children }) => {
    const { userState } = React.useContext(UserContext);

    const [lastUpdatedText, setLastUpdatedText] = React.useState('');
    const [reportTableRef, setReportTableRef] = React.useState<React.MutableRefObject<null>|null>(null);
    const [reportPdfLink, setReportPdfLink] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    
    const downloadReportCSV = () => {
        if (reportTableRef?.current) {
            const tabulator: TabulatorReferenceInterface = reportTableRef.current;
            const filename = `${pageData.acf.report_type}-export.csv`;
            tabulator.table.download('csv', filename);
        } else {
            console.log('Unable to download report CSV');
        }
    };

    React.useEffect(() => {
        if (
            userState.user?.currentFpuId > 0 &&
            pageData.acf.report_type && 
            !['fcp-settlements', 'payroll', 'payroll-ddc', 'e-statements'].includes(pageData.acf.report_type)
        ) {
            setReportPdfLink(
                ReportDataHelper.getReportPdfUrl(pageData.acf.report_type, userState.user.currentFpuId)
            );
        }
    }, [userState.user.currentFpuId]);
    
    let reportContent: any = null;
    const reportContentProps = {
        pageId: pageData.id,
        setLastUpdatedText,
        setReportTableRef,
        setLoading,
        loading
    };

    switch (pageData.acf.report_type) {
        case 'blue-dot-test':
            reportContent = <BlueDot {...reportContentProps} />;
            break;
        case 'smart-test':
            reportContent = <SmartTest {...reportContentProps} />;
            break;
        case 'degrades':
            reportContent = <Degrades {...reportContentProps} />;
            break;
        case 'quality-premiums':
            reportContent = <QualityPremiums {...reportContentProps} />;
            break;
        case 'historical-quality':
            reportContent = <HistoricalQualityComponents {...reportContentProps} />;
            break;
        case 'member-equity':
            reportContent = <MemberEquity {...reportContentProps} />;
            break;
        case 'current-quality':
            reportContent = <CurrentQuality {...reportContentProps} />;
            break;
        case 'milk-pricing-montana':
            reportContent = <MontanaPricing {...reportContentProps} reportType={pageData.acf.report_type} />;
            break;
        case 'milk-pricing-coop':
            reportContent = <CoopPricing {...reportContentProps} reportType={pageData.acf.report_type} />;
            break;
        case 'daily-components':
            reportContent = <DailyComponents {...reportContentProps} reportType={pageData.acf.report_type} />;
            break;
        case 'fcp-settlements':
            reportContent = <FCPSettlements {...reportContentProps} setReportPdfLink={setReportPdfLink} />
            break;
        case 'fcp-open-contracts':
            reportContent = <FCPOpenContracts {...reportContentProps} />
            break;
        case 'payroll':
            reportContent = <PayrollList {...reportContentProps} />;
            break;
        case 'payroll-ddc':
            reportContent = <DDCPayrollReport {...reportContentProps} />;
            break;
        case 'e-statements':
            reportContent = <EStatements {...reportContentProps} reportType={pageData.acf.report_type} />;
            break;
        case 'daily-average-production':
            reportContent = <DailyAverageProductionBase {...reportContentProps} />;
            break;
        case 'projected-milk-value':
            reportContent = <ProjectedMilkValue {...reportContentProps} />;
            break;
    }

    if (!reportContent) {
        return <NotFound />;
    }

    return (
        <PageSection>
            <Title headingLevel="h1" className="pf-u-mb-md">
                <span dangerouslySetInnerHTML={{ __html: pageData.title }} />
                {lastUpdatedText &&
                    <Text className="pf-u-ml-md" component={TextVariants.small}>{lastUpdatedText}</Text>
                }
            </Title>
            <div className="pf-c-content pf-u-mb-lg">
                {htmlParser.htmlToJSX(pageData.content)}
            </div>

            {!loading && reportTableRef?.current && 
                <Flex>
                    <FlexItem align={{ default: 'alignRight' }}>
                        <Button className="pf-u-float-right" icon={<DownloadIcon/>} onClick={downloadReportCSV}>
                            Download CSV
                        </Button>
                        {reportPdfLink &&
                            <Button component="a" className="pf-u-float-right" icon={<DownloadIcon/>} target="_blank" href={reportPdfLink}>
                                Download PDF
                            </Button>
                        }
                    </FlexItem>
                </Flex>
            }
            {reportContent}
            {pageData.acf.footer &&
                <div className="pf-c-content pf-u-mt-lg">
                    {htmlParser.htmlToJSX(pageData.acf.footer)}
                </div>
            }
        </PageSection>
    )

};

export default ReportPage;